<template>
  <div class="recent-files">
    <header class="workspace-header">
      <div class="workspace-heading">
        <div class="workspace-title-row">
          <h1 class="workspace-title">{{ $t('recentFiles.title') }}</h1>
          <span class="workspace-count">{{ $t('recentFiles.fileCount', { count: recentFiles.length }) }}</span>
        </div>
        <p class="workspace-subtitle">{{ $t('recentFiles.subtitle') }}</p>
      </div>
      <div class="workspace-actions header-buttons">
        <button @click="showYouTube" class="btn btn-secondary">
          <span class="action-icon icon-youtube" aria-hidden="true"></span>
          <span>{{ $t('recentFiles.youtubeImport') }}</span>
        </button>
        <button @click="showUploader" class="btn btn-secondary">
          <span class="action-icon icon-transcribe" aria-hidden="true"></span>
          <span>{{ $t('recentFiles.transcribe') }}</span>
        </button>
        <button @click="showTextToSpeech" class="btn btn-secondary">
          <span class="action-icon icon-speech" aria-hidden="true"></span>
          <span>{{ $t('recentFiles.textToSpeech') }}</span>
        </button>
      </div>
    </header>

    <div
      ref="scrollPanel"
      class="table-container"
      :class="{ 'is-opening-file': openingFileId }"
      @scroll="handleListScroll"
    >
      <table>
        <thead>
          <tr>
            <th>{{ $t('recentFiles.name') }}</th>
            <th>{{ $t('recentFiles.uploadedTime') }}</th>
            <th>{{ $t('recentFiles.duration') }}</th>
            <th>{{ $t('recentFiles.type') }}</th>
            <th>{{ $t('recentFiles.status') }}</th>
            <th>{{ $t('recentFiles.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading" class="table-state-row">
            <td colspan="6">
              <div class="loading">
                <i class="fas fa-spinner fa-spin"></i> {{ $t('recentFiles.loading') }}
              </div>
            </td>
          </tr>
          <tr v-else-if="error" class="table-state-row">
            <td colspan="6">
              <div class="error">
                <p>{{ error }}</p>
                <button @click="handleRetry" class="btn btn-primary retry-btn">
                  <i class="fas fa-redo"></i> {{ $t('recentFiles.retry') }}
                </button>
              </div>
            </td>
          </tr>
          <tr v-else-if="recentFiles.length === 0" class="table-state-row">
            <td colspan="6">
              <div class="no-files">
                {{ $t('recentFiles.noFiles') }}
              </div>
            </td>
          </tr>
          <template v-else>
          <tr
            v-for="file in recentFiles"
            :key="file.id"
            :class="{ 'row-opening': isOpeningFile(file), 'row-disabled': openingFileId && !isOpeningFile(file) }"
          >
            <td @click="handleFileClick(file)">
              <span class="file-name-cell" :title="displayFileName(file)">
                <i v-if="isOpeningFile(file)" class="fas fa-spinner fa-spin opening-spinner"></i>
                <span v-else :class="['file-type-mark', `type-${fileKind(file)}`]" aria-hidden="true">
                  <span class="action-icon"></span>
                </span>
                <span class="file-name-text">{{ displayFileName(file) }}</span>
                <span v-if="isOpeningFile(file)" class="opening-text">{{ $t('recentFiles.opening') }}</span>
              </span>
            </td>
            <td @click="handleFileClick(file)">{{ formatDate(file.uploadedTime) }}</td>
            <td @click="handleFileClick(file)">{{ formatDuration(file.duration) }}</td>
            <td @click="handleFileClick(file)">{{ formatType(file.type, file.sourceType) }}</td>
            <td @click="handleFileClick(file)">
              <span :class="['status-badge', `status-${file.status}`]">
                <template v-if="file.status === 'processing'">
                  <div class="progress-container">
                    <div class="progress-bar" :style="{ width: getProgressWidth(file) + '%' }"></div>
                    <span class="progress-text">{{ formatStatus(file.status) }}</span>
                  </div>
                </template>
                <template v-else>
                  {{ formatStatus(file.status) }}
                </template>
              </span>
            </td>
            <td class="actions">
              <div class="dropdown" @click.stop>
                <button
                  class="btn-icon"
                  :aria-expanded="activeDropdown === file.id"
                  :disabled="Boolean(openingFileId)"
                  @click.stop="toggleDropdown(file.id)"
                >
                  <span class="material-icons-outlined">more_horiz</span>
                </button>
                <div
                  v-show="activeDropdown === file.id"
                  class="dropdown-menu"
                  @click.stop
                  @mousedown.stop
                >
                  <button
                    @mousedown.prevent.stop="handleRename(file)"
                    class="dropdown-item"
                  >
                    {{ $t('recentFiles.rename') }}
                  </button>
                  <button
                    @mousedown.prevent.stop="handleDelete(file)"
                    class="dropdown-item delete"
                  >
                    {{ $t('recentFiles.delete') }}
                  </button>
                </div>
              </div>
            </td>
          </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div v-if="recentFiles.length > 0 && !loading && !error" class="pagination-actions">
      <button
        v-if="hasMoreFiles"
        class="btn btn-secondary load-more-btn"
        :disabled="loadingMore"
        @click="loadMoreFiles"
      >
        <i :class="['fas', loadingMore ? 'fa-spinner fa-spin' : 'fa-chevron-down']"></i>
        {{ loadingMore ? $t('recentFiles.loadingMore') : $t('recentFiles.loadMore') }}
      </button>
      <span v-else-if="recentFiles.length > pageSize" class="no-more-files">
        {{ $t('recentFiles.noMoreFiles') }}
      </span>
    </div>

    <div v-if="showUsageLimitModal" class="modal">
      <div class="modal-overlay" @click="closeUsageLimitModal"></div>
      <div class="modal-content usage-limit-modal" @click.stop>
        <h3>{{ $t('recentFiles.usageLimitTitle') }}</h3>
        <p>{{ $t('recentFiles.usageLimitMessage') }}</p>
        <div class="modal-actions">
          <button @click="closeUsageLimitModal" class="btn btn-secondary">
            {{ $t('common.cancel') }}
          </button>
          <button @click="$emit('upgrade')" class="btn btn-secondary">
            {{ $t('common.upgrade') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  collection,
  query,
  where,
  getDocs,
  limit,
  orderBy,
  startAfter,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { inject, computed, nextTick } from 'vue';
import { db } from '../../firebase';
import { useViews } from '@/composables/views'
import { useModals } from '@/composables/modals'
import { apiPost } from '@/composables/useApi'
import { formatDuration as _formatDuration } from '@/utils/format'

const recentFilesState = {
  userId: null,
  files: [],
  lastVisibleDoc: null,
  hasMoreFiles: true,
  scrollTop: 0,
  loadedOnce: false,
}

const LEGACY_TTS_FILENAME = /^tts_(?:[a-f0-9]{12}|\d{8}_\d{6})\.mp3$/i

function buildTTSDisplayName(text) {
  const normalized = String(text || '')
    .replace(/\s+/g, ' ')
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[. ]+$/g, '')

  if (!normalized) return ''

  const characters = Array.from(normalized)
  const summary = characters.length > 56
    ? `${characters.slice(0, 56).join('').trimEnd()}…`
    : normalized
  return `${summary}.mp3`
}

function getTTSNamingText(file) {
  if (file?.originalText) return file.originalText

  const subtitleSources = [
    file?.completeSubtitles,
    file?.subtitles,
    file?.originalSubtitles,
    file?.practiceSubtitles,
  ]
  for (const subtitles of subtitleSources) {
    if (!Array.isArray(subtitles)) continue
    const text = subtitles
      .map((item) => (
        typeof item === 'string'
          ? item
          : item?.text || item?.content || item?.originalText || ''
      ))
      .filter(Boolean)
      .join(' ')
    if (text) return text
  }
  return ''
}

function getDisplayFileName(file) {
  const name = String(file?.name || '').trim()
  if (LEGACY_TTS_FILENAME.test(name)) {
    return buildTTSDisplayName(getTTSNamingText(file)) || name
  }
  return name
}

export default {
  name: 'RecentFiles',
  emits: ['rename-file', 'delete-file'],
  data() {
    return {
      pageSize: 30,
      recentFiles: [],
      loading: true,
      loadingMore: false,
      hasMoreFiles: true,
      lastVisibleDoc: null,
      error: null,
      activeDropdown: null,
      openingFileId: null,
      refreshInterval: null,
      showUsageLimitModal: false,
      processingProgress: new Map(),
      progressIntervals: new Map(),
      reconcilingFiles: new Set(),
    }
  },
  setup() {
    const dialog = inject('dialog')
    const { contentProps, handleViewChange } = useViews()
    const { showTranscribeInterface, showYouTubeImporter, showTextToSpeech } = useModals()
    const viewParams = computed(() => ({ refresh: contentProps.value?.refresh }))
    return {
      dialog,
      viewParams,
      _handleViewChange: handleViewChange,
      _showTranscribeInterface: showTranscribeInterface,
      _showYouTubeImporter: showYouTubeImporter,
      _showTextToSpeech: showTextToSpeech
    }
  },
  async mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    this.cleanupRecentFiles();
  },
  beforeUnmount() {
    this.cleanupRecentFiles();
  },
  methods: {
    cleanupRecentFiles() {
      this.persistRecentFilesState();
      this.stopAutoRefresh();
      document.removeEventListener('click', this.handleClickOutside);
    },
    persistRecentFilesState() {
      const auth = getAuth();
      if (!auth.currentUser) return;

      recentFilesState.userId = auth.currentUser.uid;
      recentFilesState.files = [...this.recentFiles];
      recentFilesState.lastVisibleDoc = this.lastVisibleDoc;
      recentFilesState.hasMoreFiles = this.hasMoreFiles;
      recentFilesState.loadedOnce = this.recentFiles.length > 0 || !this.hasMoreFiles;
      const scrollPanel = this.getScrollPanel();
      recentFilesState.scrollTop = scrollPanel
        ? scrollPanel.scrollTop
        : recentFilesState.scrollTop;
    },
    async restoreRecentFilesState() {
      const auth = getAuth();
      if (!auth.currentUser || recentFilesState.userId !== auth.currentUser.uid || !recentFilesState.loadedOnce) {
        return false;
      }

      this.recentFiles = [...recentFilesState.files];
      this.lastVisibleDoc = recentFilesState.lastVisibleDoc;
      this.hasMoreFiles = recentFilesState.hasMoreFiles;
      this.loading = false;
      this.loadingMore = false;
      this.error = null;

      await nextTick();
      const scrollPanel = this.getScrollPanel();
      if (scrollPanel) {
        scrollPanel.scrollTop = recentFilesState.scrollTop || 0;
      }

      return true;
    },
    clearRecentFilesState() {
      recentFilesState.userId = null;
      recentFilesState.files = [];
      recentFilesState.lastVisibleDoc = null;
      recentFilesState.hasMoreFiles = true;
      recentFilesState.scrollTop = 0;
      recentFilesState.loadedOnce = false;
    },
    getScrollPanel() {
      return this.$refs.scrollPanel || null;
    },
    handleClickOutside() {
      this.activeDropdown = null;
      if (this.hasProcessingFiles) {
        this.startAutoRefresh();
      }
    },
    async fetchRecentFiles({ reset = true, pageSize = this.pageSize, showLoading = true } = {}) {
      const auth = getAuth()
      if (!auth.currentUser) {
        this.recentFiles = []
        this.loading = false
        return
      }

      try {
        if (reset) {
          this.lastVisibleDoc = null
          if (showLoading) {
            this.loading = true
          }
        }
        this.error = null

        const constraints = [
          where('userId', '==', auth.currentUser.uid),
          orderBy('uploadedTime', 'desc'),
        ]

        if (!reset && this.lastVisibleDoc) {
          constraints.push(startAfter(this.lastVisibleDoc))
        }

        constraints.push(limit(pageSize))

        const q = query(
          collection(db, 'files'),
          ...constraints
        )
        const querySnapshot = await getDocs(q)
        const files = querySnapshot.docs.map(this.normalizeFile)

        if (this.activeDropdown && !showLoading) {
          return
        }

        if (reset) {
          this.recentFiles = files
        } else {
          const existingIds = new Set(this.recentFiles.map(file => file.id))
          this.recentFiles = [
            ...this.recentFiles,
            ...files.filter(file => !existingIds.has(file.id)),
          ]
        }

        this.lastVisibleDoc = querySnapshot.docs[querySnapshot.docs.length - 1] || this.lastVisibleDoc
        this.hasMoreFiles = querySnapshot.docs.length === pageSize
        this.persistRecentFilesState()
        this.$nextTick(this.loadMoreIfNeeded)
      } catch (error) {
        this.error = error.message
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },
    normalizeFile(docSnapshot) {
      const data = docSnapshot.data()
      return {
        id: docSnapshot.id,
        ...data,
        uploadedTime: data.uploadedTime?.toDate() || data.createdAt?.toDate() || null
      }
    },
    refresh() {
      this.clearRecentFilesState();
      const scrollPanel = this.getScrollPanel();
      if (scrollPanel) {
        scrollPanel.scrollTop = 0;
      }
      this.fetchRecentFiles({ reset: true });
    },
    displayFileName(file) {
      return getDisplayFileName(file)
    },
    async loadMoreFiles() {
      if (this.loading || this.loadingMore || !this.hasMoreFiles) return

      this.loadingMore = true
      await this.fetchRecentFiles({ reset: false, showLoading: false })
    },
    handleListScroll(event) {
      recentFilesState.scrollTop = event.target.scrollTop;

      const distanceToBottom = event.target.scrollHeight - event.target.scrollTop - event.target.clientHeight;
      if (distanceToBottom < 160) {
        this.loadMoreFiles();
      }
    },
    loadMoreIfNeeded() {
      const scrollPanel = this.getScrollPanel();
      if (!scrollPanel || this.loading || this.loadingMore || !this.hasMoreFiles) return;

      if (scrollPanel.scrollHeight <= scrollPanel.clientHeight + 80) {
        this.loadMoreFiles();
      }
    },
    startAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
      }
      this.refreshInterval = setInterval(() => {
        this.fetchRecentFiles({
          reset: true,
          pageSize: Math.max(this.recentFiles.length, this.pageSize),
          showLoading: false
        });
      }, 3000); // 每3秒刷新一次
    },
    stopAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return '';
      if (timestamp instanceof Date) {
        return timestamp.toLocaleString();
      }
      if (timestamp.seconds) {
        return new Date(timestamp.seconds * 1000).toLocaleString();
      }
      return new Date(timestamp).toLocaleString();
    },
    formatDuration(duration) {
      return _formatDuration(duration)
    },
    fileKind(file) {
      if (file.sourceType === 'youtube') return 'youtube'
      return file.type === 'tts' ? 'tts' : 'stt'
    },
    formatType(type, sourceType) {
      if (sourceType === 'youtube') return this.$t('recentFiles.typeYouTube')
      const typeMap = {
        'stt': this.$t('recentFiles.typeSTT'),
        'tts': this.$t('recentFiles.typeTTS'),
        'tts-stt': this.$t('recentFiles.typeTTSSTT')
      }
      return typeMap[type] || type
    },
    formatStatus(status) {
      const statusMap = {
        completed: this.$t('recentFiles.completed'),
        processing: this.$t('recentFiles.processing'),
        pending: this.$t('recentFiles.pending'),
        failed: this.$t('recentFiles.error')
      }
      return statusMap[status] || status
    },

    handleRetry() {
      this.error = null;
      this.fetchRecentFiles({ reset: true });
    },
    toggleDropdown(fileId) {
      if (this.openingFileId) return;

      this.activeDropdown = this.activeDropdown === fileId ? null : fileId;
      if (this.activeDropdown) {
        this.stopAutoRefresh();
      } else if (this.hasProcessingFiles) {
        this.startAutoRefresh();
      }
    },
    async handleRename(file) {
      this.dialog.openRenameDialog({
        title: this.$t('recentFiles.dialog.rename.title'),
        placeholder: this.$t('recentFiles.dialog.rename.placeholder'),
        confirmText: this.$t('recentFiles.dialog.rename.confirm'),
        cancelText: this.$t('recentFiles.dialog.rename.cancel'),
        emit: async (event, value) => {
          if (event === 'renameFile') {
            try {
              await apiPost(`/api/files/${file.id}/rename`, {
                name: value
              });
              
              // 刷新文件列表
              await this.fetchRecentFiles({ reset: true });
              
              // 发出重命名事件
              this.$emit('rename-file', { id: file.id, newName: value });
            } catch (error) {
              this.error = 'Failed to rename file: ' + error.message;
            }
          }
        }
      }, this.displayFileName(file));
      this.activeDropdown = null;
      if (this.hasProcessingFiles) {
        this.startAutoRefresh();
      }
    },
    async handleDelete(file) {
      this.dialog.openDeleteDialog({
        title: this.$t('recentFiles.dialog.delete.title'),
        message: this.$t('recentFiles.dialog.delete.message'),
        warning: this.$t('recentFiles.dialog.delete.warning'),
        confirmText: this.$t('recentFiles.dialog.delete.confirm'),
        cancelText: this.$t('recentFiles.dialog.delete.cancel'),
        emit: async (event) => {
          if (event === 'deleteFile') {
            try {
              await apiPost(`/api/files/${file.id}/delete`, {});
              
              // 刷新文件列表
              await this.fetchRecentFiles({ reset: true });
              
              // 发出删除事件
              this.$emit('delete-file', file.id);
            } catch (error) {
              this.error = 'Failed to delete file: ' + error.message;
            }
          }
        }
      }, this.displayFileName(file));
      this.activeDropdown = null;
      if (this.hasProcessingFiles) {
        this.startAutoRefresh();
      }
    },
    showUploader() {
      this._showTranscribeInterface()
    },
    showYouTube() {
      this._showYouTubeImporter()
    },
    showTextToSpeech() {
      this._showTextToSpeech()
    },
    closeUsageLimitModal() {
      this.showUsageLimitModal = false;
    },
    async handleFileClick(file) {
      if (this.openingFileId || file.status !== 'completed') {
        return;
      }

      try {
        this.openingFileId = file.id;
        this.error = null;
        this.activeDropdown = null;
        this.stopAutoRefresh();

        const isYouTube = file.sourceType === 'youtube' && file.youtube?.videoId
        const data = isYouTube
          ? { url: '' }
          : await apiPost('/api/get-signed-url', {
              filePath: file.filePath,
              fileId: file.id
            });
        
        // 直接切换到音频播放器
        this._handleViewChange('AudioPlayer', {
          audioSrc: isYouTube ? '' : data.url,
          youtubeVideoId: isYouTube ? file.youtube.videoId : '',
          posterSrc: isYouTube ? file.youtube.thumbnailUrl : '',
          audioName: this.displayFileName(file),
          audioId: file.id,
          taskType: file.type,
          originalSubtitles: file.originalSubtitles,
          subtitles: file.subtitles,
          practiceSubtitles: file.practiceSubtitles,
          shortPracticeSubtitles: file.shortPracticeSubtitles,
          pauseSubtitles: file.pauseSubtitles,
          completeSubtitles: file.completeSubtitles,
          words: file.words,
          useSmartSplit: false,
          originalText: file.originalText,
          filePath: file.filePath,
          duration: file.duration,
          materialId: file.materialId,
          materialBook: file.materialBook,
          isDemo: false,
          returnView: 'RecentFiles',
          returnParams: {}
        });
      } catch (error) {
        this.error = 'Failed to load audio file: ' + error.message;
        this.openingFileId = null;
        if (this.hasProcessingFiles) {
          this.startAutoRefresh();
        }
      }
    },
    isOpeningFile(file) {
      return this.openingFileId === file.id;
    },
    isLastRow(file) {
      const index = this.recentFiles.findIndex(f => f.id === file.id);
      return index === this.recentFiles.length - 1;
    },
    getProgressWidth(file) {
      return this.processingProgress.get(file.id) || 0;
    },
    startProgressAnimation(file) {
      // 如果已经有定时器在运行，先清除
      if (this.progressIntervals.has(file.id)) {
        clearInterval(this.progressIntervals.get(file.id));
      }

      // 设置初始进度
      this.processingProgress.set(file.id, 0);

      // 计算每秒增加的进度
      const duration = file.duration/10 || 10; // 如果没有 duration，则 10 秒
      const totalSteps = 99; // 最大进度为 99%
      const intervalTime = 50; // 每 50ms 更新一次
      const progressPerStep = (totalSteps * intervalTime) / (duration * 1000);

      // 创建定时器
      const interval = setInterval(() => {
        const currentProgress = this.processingProgress.get(file.id) || 0;
        if (currentProgress >= 99 || file.status !== 'processing') {
          clearInterval(interval);
          this.progressIntervals.delete(file.id);
        } else {
          this.processingProgress.set(file.id, Math.min(99, currentProgress + progressPerStep));
        }
      }, intervalTime);

      // 保存定时器引用
      this.progressIntervals.set(file.id, interval);
    },

    stopProgressAnimation(fileId) {
      if (this.progressIntervals.has(fileId)) {
        clearInterval(this.progressIntervals.get(fileId));
        this.progressIntervals.delete(fileId);
      }
      this.processingProgress.delete(fileId);
    },
    isTranscriptionLeaseExpired(file) {
      const lease = file?.transcriptionLeaseExpiresAt;
      if (!lease) return false;
      const leaseDate = typeof lease.toDate === 'function'
        ? lease.toDate()
        : new Date(lease.seconds ? lease.seconds * 1000 : lease);
      return Number.isFinite(leaseDate.getTime()) && leaseDate.getTime() <= Date.now();
    },
    isTTSLeaseExpired(file) {
      const lease = file?.ttsLeaseExpiresAt;
      if (!lease) return false;
      const leaseDate = typeof lease.toDate === 'function'
        ? lease.toDate()
        : new Date(lease.seconds ? lease.seconds * 1000 : lease);
      return Number.isFinite(leaseDate.getTime()) && leaseDate.getTime() <= Date.now();
    },
    async reconcileTimedOutTranscription(file) {
      if (this.reconcilingFiles.has(file.id)) return;
      this.reconcilingFiles.add(file.id);
      try {
        const result = await apiPost('/api/transcribe/reconcile', {
          fileId: file.id
        });
        if (result.reconciled) {
          await this.fetchRecentFiles({
            reset: true,
            pageSize: Math.max(this.recentFiles.length, this.pageSize),
            showLoading: false
          });
        }
      } catch (error) {
        console.warn('Unable to reconcile timed-out transcription', error);
      } finally {
        this.reconcilingFiles.delete(file.id);
      }
    },
    async reconcileTimedOutTTS(file) {
      if (this.reconcilingFiles.has(file.id)) return;
      this.reconcilingFiles.add(file.id);
      try {
        const result = await apiPost('/api/tts/reconcile', {
          fileId: file.id
        });
        if (result.reconciled) {
          await this.fetchRecentFiles({
            reset: true,
            pageSize: Math.max(this.recentFiles.length, this.pageSize),
            showLoading: false
          });
        }
      } catch (error) {
        console.warn('Unable to reconcile timed-out TTS job', error);
      } finally {
        this.reconcilingFiles.delete(file.id);
      }
    },
  },
  computed: {
    hasProcessingFiles() {
      return this.recentFiles.some(file => file.status === 'processing');
    }
  },
  watch: {
    // 监听文件状态变化
    recentFiles: {
      handler(newFiles) {
        newFiles.forEach(file => {
          if (file.status === 'processing' && !this.progressIntervals.has(file.id)) {
            this.startProgressAnimation(file);
          } else if (file.status !== 'processing') {
            this.stopProgressAnimation(file.id);
          }
          if (
            file.status === 'processing'
            && this.isTranscriptionLeaseExpired(file)
          ) {
            this.reconcileTimedOutTranscription(file);
          } else if (
            file.status === 'processing'
            && this.isTTSLeaseExpired(file)
          ) {
            this.reconcileTimedOutTTS(file);
          }
        });
        
        // 检查是否有处理中的文件
        const hasProcessing = newFiles.some(file => file.status === 'processing');
        if (hasProcessing && !this.activeDropdown) {
          this.startAutoRefresh();
        } else {
          this.stopAutoRefresh();
        }
      },
      deep: true
    },
    // 监听用户状态变化
    '$root.user': {
      immediate: true,
      handler(newUser) {
        if (newUser) {
          this.restoreRecentFilesState().then((restored) => {
            if (!restored) {
              this.fetchRecentFiles({ reset: true });
            }
          });
        } else {
          this.clearRecentFilesState();
          this.recentFiles = [];
          this.lastVisibleDoc = null;
          this.hasMoreFiles = true;
          this.loading = false;
        }
      }
    },
    // 监听刷新参数
    viewParams: {
      handler(newParams) {
        if (newParams && newParams.refresh) {
          this.clearRecentFilesState();
          // 清除之前的定时器
          this.stopAutoRefresh();
          // 立即刷新
          this.fetchRecentFiles({ reset: true });
          // 如果有处理中的文件，启动自动刷新
          if (this.hasProcessingFiles) {
            this.startAutoRefresh();
          }
        }
      },
      immediate: true
    }
  }
}
</script>

<style scoped>
@import '../../styles/recent-files.css';
</style>
